import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { topUpFeeCents } from '../utils/topUpFee';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import PageShell from '../components/chrome/PageShell';
import { DevCardElement, CashOutModal } from '../components';
import { walletService, friendService } from '../services';
import { useUser } from '../context';
import useMediaQuery from '../utils/useMediaQuery';
import { isStripeDevMode, mockConfirmCardSetup } from '../utils/stripeDevMode';
import { A, Avatar, AVATAR_TINTS, Card, EmptyCard, FieldLabel, Input, PageTitle, Pill, RADIUS, StatusPill, fillFor, money } from '../components/account/kit';
import { decorate, groupTransactions, filterCounts, matchesFilter, signedMoney, receiptPath } from '../components/wallet/walletModel';

const PAGE_SIZE = 50;

const withdrawalWhen = (iso) => {
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const day = new Date(d); day.setHours(0, 0, 0, 0);
  const diff = Math.round((today - day) / 86400000);
  if (diff === 0) return 'TODAY';
  if (diff === 1) return 'YESTERDAY';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined }).toUpperCase();
};

const cardElementOptions = {
  style: {
    base: {
      fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: '14px',
      color: '#000000',
      '::placeholder': { color: '#9ca3af' },
    },
    invalid: { color: '#dc2626', iconColor: '#dc2626' },
  },
};

/**
 * /wallet (Sept 2026 redesign, handoff §2): the balance hero with + ADD MONEY / CASH OUT,
 * three stat cards, and the transaction history grouped by day with ALL · IN · OUT · HELD
 * filters and expandable rows. Type names come from `walletModel` (readable labels, the
 * HELD pill instead of "(held until…)" suffixes, a real minus sign, no red for money out).
 *
 * CASH OUT opens the cash-out modal for everyone (Oct 2026: the wallet is the only way
 * money leaves — hosts and guests alike, Stripe's payout fee the only charge); `?stripe=return`
 * reopens it after Connect onboarding. PAYOUTS below the hero lists past cash-outs with
 * their status, which used to live on the Settings page.
 */
export default function WalletPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { walletBalance, refreshWallet } = useUser();
  const stripe = useStripe();
  const elements = useElements();
  const devCardRef = useRef();
  const devMode = isStripeDevMode();
  const phone = useMediaQuery('(max-width: 639px)');
  const mid = useMediaQuery('(max-width: 899px)');

  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);

  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardError, setCardError] = useState(null);
  const [topUpError, setTopUpError] = useState(null);

  const [p2pEnabled, setP2pEnabled] = useState(true);
  const [showCashOutModal, setShowCashOutModal] = useState(false);
  const cashOutRequestId = useRef(null);

  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (searchParams.get('stripe') === 'return') setShowCashOutModal(true);
  }, [searchParams]);

  const fetchWallet = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [walletResult, transactionsResult, featuresResult, withdrawalsResult] = await Promise.all([
        walletService.getWallet(),
        walletService.getTransactions(0, PAGE_SIZE),
        walletService.getFeatures(),
        walletService.getWithdrawals(0, 20),
      ]);
      if (walletResult.success) setWallet(walletResult.data);
      else setError(walletResult.error || 'Failed to load wallet');
      if (transactionsResult.success) {
        const list = transactionsResult.data || [];
        setTransactions(list);
        setHasMore(list.length >= PAGE_SIZE);
      }
      if (featuresResult.success) setP2pEnabled(!!featuresResult.data?.p2pTransfersEnabled);
      if (withdrawalsResult?.success) setWithdrawals(withdrawalsResult.data || []);
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchWallet(); }, [fetchWallet]);

  const loadOlder = async () => {
    setLoadingMore(true);
    const r = await walletService.getTransactions(transactions.length, PAGE_SIZE);
    if (r.success) {
      const list = r.data || [];
      setTransactions((prev) => [...prev, ...list]);
      setHasMore(list.length >= PAGE_SIZE);
    }
    setLoadingMore(false);
  };

  // Legacy Stripe Checkout redirect params.
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setNotice('Money added.');
      window.history.replaceState({}, '', '/wallet');
      fetchWallet();
      refreshWallet();
      setTimeout(() => setNotice(null), 5000);
    } else if (searchParams.get('canceled') === 'true') {
      setError('Payment was canceled');
      window.history.replaceState({}, '', '/wallet');
    }
  }, [searchParams, fetchWallet, refreshWallet]);

  const handleTopUp = async () => {
    const amount = parseFloat(topUpAmount);
    if (Number.isNaN(amount) || amount < 1 || amount > 1000) return;
    if (!devMode && (!stripe || !elements)) return;
    setIsProcessing(true);
    setTopUpError(null);
    try {
      const amountInCents = Math.round(amount * 100);
      const result = await walletService.createPaymentIntent(amountInCents);
      if (!result.success) { setTopUpError(result.error || 'Failed to create payment'); return; }
      const { clientSecret } = result.data;
      const done = async () => {
        setNotice('Money added.');
        setShowTopUp(false);
        setTopUpAmount('');
        await fetchWallet();
        refreshWallet();
        setTimeout(() => setNotice(null), 5000);
      };
      if (devMode) {
        const cardData = devCardRef.current?.getCardData();
        if (!cardData || !cardData.number) { setTopUpError('Please enter card details'); return; }
        const mockResult = mockConfirmCardSetup(cardData);
        if (mockResult.error) { setTopUpError(mockResult.error.message); return; }
        const topUpResult = await walletService.topUp(amountInCents);
        if (topUpResult.success) { devCardRef.current?.clear(); await done(); } else setTopUpError(topUpResult.error || 'Failed to add funds');
      } else {
        const cardElement = elements.getElement(CardElement);
        const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, { payment_method: { card: cardElement } });
        if (stripeError) setTopUpError(stripeError.message);
        else if (paymentIntent.status === 'succeeded') { await walletService.confirmTopUp(paymentIntent.id); await done(); }
      }
    } catch {
      setTopUpError('Failed to process payment');
    } finally {
      setIsProcessing(false);
    }
  };

  const cashOut = () => setShowCashOutModal(true);

  const now = useMemo(() => new Date(), [transactions]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = useMemo(() => transactions.map((t) => decorate(t, now)), [transactions, now]);
  const counts = useMemo(() => filterCounts(transactions), [transactions]);
  const visible = useMemo(() => rows.filter((r) => matchesFilter(r, filter)), [rows, filter]);
  const groups = useMemo(() => groupTransactions(visible), [visible]);

  const balance = wallet ? wallet.balance : (walletBalance || 0);
  const escrow = wallet?.escrowBalance || 0;

  const tab = (key, label) => {
    const on = filter === key;
    return (
      <button
        key={key}
        type="button"
        onClick={() => { setFilter(key); setOpen(null); }}
        aria-pressed={on}
        className={on ? 'pr-acct-pill' : 'pr-acct-pill pr-acct-pill-white'}
        style={{ height: 40, padding: '0 16px', border: '2px solid transparent', borderRadius: RADIUS.pill, background: on ? fillFor('on') : A.white, color: on ? '#fff' : A.ink, fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', cursor: 'pointer', whiteSpace: 'nowrap', boxSizing: 'border-box', transition: 'background-color .2s, color .2s' }}
      >
        {label}
      </button>
    );
  };

  const presetAmounts = [5, 10, 25, 50, 100];
  const topUpValid = topUpAmount !== '' && parseFloat(topUpAmount) >= 1 && parseFloat(topUpAmount) <= 1000;

  return (
    <PageShell bare>
      <main style={{ maxWidth: 1120, margin: '0 auto', padding: phone ? '20px 16px 64px' : '32px 28px 80px', boxSizing: 'border-box', color: A.ink }}>
        <PageTitle
          phone={phone}
          title="WALLET"
          meta={<span>YOUR BALANCE</span>}
          right={(
            <button type="button" onClick={() => navigate('/settings#money')} style={{ padding: 0, border: 0, background: 'none', fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: A.grey500, cursor: 'pointer' }}>
              PAYOUT SETTINGS →
            </button>
          )}
        />

        {notice && (
          <div role="status" style={{ marginBottom: 12, padding: '12px 18px', borderRadius: 16, background: A.ink, color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '.14em' }}>
            {notice.toUpperCase()}
          </div>
        )}
        {error && !isLoading && (
          <div role="alert" style={{ marginBottom: 12, padding: '12px 18px', borderRadius: 16, background: A.white, color: A.danger, fontSize: 11, fontWeight: 700, letterSpacing: '.1em', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>{error}</span>
            <Pill kind="secondary" onClick={fetchWallet} style={{ height: 32, padding: '0 14px', fontSize: 10 }}>RETRY</Pill>
          </div>
        )}

        {/* Hero */}
        <div style={{ display: 'grid', gridTemplateColumns: mid ? 'minmax(0,1fr)' : 'minmax(0,1.3fr) minmax(0,1fr)', gap: 12, marginBottom: 32 }}>
          <Card pad={phone ? 20 : 28} gap={20}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: A.grey500 }}>CURRENT BALANCE</span>
              <span data-testid="wallet-balance" style={{ fontSize: phone ? 44 : 64, fontWeight: 700, lineHeight: 1, letterSpacing: '-.01em' }}>{isLoading && !wallet ? '—' : money(balance)}</span>
              {escrow > 0 && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: A.grey600 }}>
                  <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: A.escrow }} />
                  + {money(escrow)} held in escrow
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <Pill kind="primary" height={44} style={{ padding: '0 22px' }} onClick={() => { setShowTopUp((v) => !v); setTopUpError(null); }}>
                {showTopUp ? 'CANCEL' : '+ ADD MONEY'}
              </Pill>
              {p2pEnabled && <SendMoneyPill wallet={wallet} onSent={async () => { setNotice('Money sent.'); await fetchWallet(); refreshWallet(); setTimeout(() => setNotice(null), 5000); }} />}
              <Pill kind="secondary" height={44} style={{ padding: '0 22px' }} onClick={cashOut}>CASH OUT</Pill>
            </div>

            {showTopUp && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 20, borderTop: `2px solid ${A.canvas}` }}>
                <FieldLabel htmlFor="topup-amt">ADD AMOUNT ($)</FieldLabel>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {presetAmounts.map((amount) => {
                    const on = topUpAmount === amount.toString();
                    return (
                      <button key={amount} type="button" onClick={() => setTopUpAmount(amount.toString())} aria-pressed={on} style={{ height: 32, padding: '0 14px', border: '2px solid transparent', borderRadius: RADIUS.pill, background: on ? fillFor('on') : A.off, color: on ? '#fff' : A.ink, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '.08em', cursor: 'pointer', boxSizing: 'border-box' }}>
                        ${amount}
                      </button>
                    );
                  })}
                </div>
                <Input id="topup-amt" inputMode="decimal" value={topUpAmount} onChange={(e) => setTopUpAmount(e.target.value.replace(/[^\d.]/g, ''))} placeholder="0.00" style={{ height: 56, fontSize: 22, fontWeight: 700, width: '100%' }} />
                <span style={{ fontSize: 10, letterSpacing: '.08em', color: A.grey500 }}>Min $1.00 · Max $1,000.00 · Funds are added in CAD.</span>
                {topUpValid && (() => {
                  const cents = Math.round(parseFloat(topUpAmount) * 100);
                  const fee = topUpFeeCents(cents);
                  return (
                    <span data-testid="topup-fee" style={{ fontSize: 11, letterSpacing: '.06em', color: A.ink }}>
                      Card processing fee {money(fee)} · Your card is charged {money(cents + fee)}
                    </span>
                  );
                })()}
                <FieldLabel>CARD DETAILS</FieldLabel>
                <div style={{ padding: '14px 18px', borderRadius: 16, background: A.off, border: `2px solid ${cardError ? A.danger : 'transparent'}` }}>
                  {devMode ? (
                    <DevCardElement ref={devCardRef} onChange={(e) => setCardError(e.error ? e.error.message : null)} disabled={isProcessing} />
                  ) : (
                    <CardElement options={cardElementOptions} onChange={(e) => setCardError(e.error ? e.error.message : null)} />
                  )}
                </div>
                {cardError && <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: A.danger }}>{cardError}</span>}
                {topUpError && <span role="alert" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: A.danger }}>{topUpError}</span>}
                {devMode && <span style={{ fontSize: 10, color: A.grey500 }}>Dev mode: 4242 4242 4242 4242 succeeds, 4000 0000 0000 0002 declines.</span>}
                <Pill kind="primary" height={48} style={{ width: '100%', fontSize: 12, letterSpacing: '.16em' }} onClick={handleTopUp} disabled={!topUpValid || isProcessing || (!devMode && !stripe)}>
                  {isProcessing ? 'PROCESSING…' : `ADD ${topUpValid ? money(Math.round(parseFloat(topUpAmount) * 100)) : '$0.00'}`}
                </Pill>
              </div>
            )}
          </Card>

          <div style={{ display: 'grid', gridTemplateColumns: mid ? 'repeat(3,minmax(0,1fr))' : 'minmax(0,1fr)', gap: 12 }}>
            <Stat label="EARNED" value={money(wallet?.totalEarned)} color={A.moneyIn} />
            <Stat label="SPENT" value={money(wallet?.totalSpent)} />
            <Stat label="FLAKE FEES" value={money(wallet?.totalFlakeFees)} />
          </div>
        </div>

        {/* Payouts — past cash-outs with their status (was the Settings WITHDRAWAL HISTORY card) */}
        {withdrawals.length > 0 && (
          <section data-testid="payouts" style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32, minWidth: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '.12em' }}>PAYOUTS</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {withdrawals.map((w) => {
                const st = String(w.status || '').toLowerCase();
                const pill = st === 'completed' ? <StatusPill bg={A.verified}>COMPLETED</StatusPill>
                  : st === 'failed' ? <StatusPill bg={A.danger}>FAILED</StatusPill>
                  : <StatusPill>PROCESSING</StatusPill>;
                const meta = st === 'completed' ? `${withdrawalWhen(w.createdAt)} · STRIPE FEE ${money(w.feeCents)} · NET ${money(w.netAmountCents)}`
                  : st === 'failed' ? `${withdrawalWhen(w.createdAt)} · RETURNED TO YOUR WALLET`
                  : `${withdrawalWhen(w.createdAt)} · ARRIVES IN 2–3 DAYS`;
                return (
                  <div key={w.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 18px', borderRadius: 16, background: A.white }}>
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>{money(w.amountCents)}</span>
                      <span style={{ fontSize: 10, letterSpacing: '.1em', color: A.grey500 }}>{meta}</span>
                    </span>
                    {pill}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Transaction history */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: 24, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '.12em', whiteSpace: 'nowrap' }}>TRANSACTION HISTORY</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {tab('all', 'ALL')}
              {tab('in', `IN · ${counts.in}`)}
              {tab('out', `OUT · ${counts.out}`)}
              {tab('held', `HELD · ${counts.held}`)}
            </div>
          </div>

          {isLoading && transactions.length === 0 && (
            <div style={{ padding: '40px 0', textAlign: 'center', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', color: A.grey500 }}>LOADING…</div>
          )}

          {!isLoading && visible.length === 0 && <EmptyCard title="NOTHING HERE YET." pad="56px 24px" />}

          {groups.map((g) => (
            <div key={g.label} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {g.showHead && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.12em', whiteSpace: 'nowrap' }}>{g.label}</span>
                  <span style={{ fontSize: 11, letterSpacing: '.1em', color: g.net > 0 ? A.moneyIn : A.grey500, whiteSpace: 'nowrap' }}>NET {signedMoney(g.net)}</span>
                  <span aria-hidden style={{ flex: 1, height: 2, background: A.ink }} />
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {g.items.map((t) => (
                  <TransactionRow key={t.id} t={t} phone={phone} open={open === t.id} onToggle={() => setOpen(open === t.id ? null : t.id)} onReceipt={receiptPath(t) ? () => navigate(receiptPath(t)) : null} />
                ))}
              </div>
            </div>
          ))}

          {hasMore && (
            <Pill kind="white" onClick={loadOlder} disabled={loadingMore} style={{ alignSelf: 'center', height: 42, padding: '0 22px' }}>
              {loadingMore ? 'LOADING…' : 'LOAD OLDER…'}
            </Pill>
          )}
        </section>
      </main>

      <CashOutModal
        isOpen={showCashOutModal}
        onClose={() => setShowCashOutModal(false)}
        currentBalanceCents={walletBalance || 0}
        onConfirm={async (amountCents) => {
          // A stable id per attempt: a retry after a timeout resumes the same cash-out (never two).
          // The id belongs to ONE amount — the server refuses an id reused for different terms —
          // so a changed amount gets a fresh id (and the server refuses it while the first is
          // still processing, which is the sentence the person should read).
          if (!cashOutRequestId.current || cashOutRequestId.current.amountCents !== amountCents) {
            cashOutRequestId.current = { amountCents, id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : null };
          }
          const r = await walletService.executeGuestCashOut(amountCents, cashOutRequestId.current.id);
          if (r.success || r.status === 400) cashOutRequestId.current = null;
          // 409 = still processing with Stripe: the debit is real, so the balance shown must drop.
          if (r.status === 409) await refreshWallet();
          if (r.success) {
            setShowCashOutModal(false);
            await refreshWallet();
            await fetchWallet();
          }
          return r;
        }}
      />
    </PageShell>
  );
}

function Stat({ label, value, color = A.ink }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6, padding: '18px 20px', borderRadius: RADIUS.panel, background: A.white, minWidth: 0 }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: A.grey500 }}>{label}</span>
      <span style={{ fontSize: 24, fontWeight: 700, color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
    </div>
  );
}

/** One transaction card: tile · type pill + time · description · amount, expanding to the detail strip. */
export function TransactionRow({ t, phone, open, onToggle, onReceipt }) {
  const inbound = t.amount > 0;
  return (
    <article
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={onToggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      className="pr-acct-row pr-acct-row-hover"
      style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: phone ? 12 : '14px 18px 14px 14px', borderRadius: RADIUS.row, background: A.white, cursor: 'pointer', transition: 'background-color .2s' }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '48px minmax(0,1fr) auto', gap: 14, alignItems: 'center' }}>
        {t.imageUrl ? (
          <img src={t.imageUrl} alt="" style={{ width: 48, height: 48, borderRadius: 14, objectFit: 'cover', display: 'block' }} />
        ) : (
          <span aria-hidden style={{ width: 48, height: 48, borderRadius: 14, background: t.glyph.bg, color: t.glyph.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800 }}>{t.glyph.glyph}</span>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <StatusPill bg={inbound ? A.ink : A.off} fg={inbound ? '#fff' : A.ink}>{t.label}</StatusPill>
            {t.held && <StatusPill bg={A.held} fg={A.ink} style={{ letterSpacing: '.12em' }}>HELD</StatusPill>}
            <span style={{ fontSize: 10, letterSpacing: '.1em', color: A.grey400 }}>{t.when}</span>
          </div>
          <span style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.4, overflow: 'hidden', whiteSpace: open ? 'normal' : 'nowrap', textOverflow: 'ellipsis', textWrap: 'pretty' }}>{t.desc}</span>
          {t.sponsorAttribution && <span style={{ fontSize: 10, letterSpacing: '.08em', color: A.grey500 }}>{t.sponsorAttribution}</span>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: inbound ? A.moneyIn : A.ink, whiteSpace: 'nowrap' }}>{signedMoney(t.amount)}</span>
          <span style={{ fontSize: 10, letterSpacing: '.08em', color: A.grey400, whiteSpace: 'nowrap' }}>BAL {money(t.balanceAfter)}</span>
        </div>
      </div>
      {open && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 16px', marginLeft: phone ? 0 : 62, padding: '12px 14px', borderRadius: 14, background: A.canvas, fontSize: 10, letterSpacing: '.1em', color: A.grey600 }}>
          <span>{t.date}</span>
          <span>REF {t.ref}</span>
          {t.held && <span style={{ color: A.ink, fontWeight: 700 }}>RELEASES WHEN THE EVENT ENDS</span>}
          <span style={{ flex: 1 }} />
          {onReceipt && (
            <button type="button" onClick={(e) => { e.stopPropagation(); onReceipt(); }} className="pr-acct-invert" style={{ height: 30, padding: '0 12px', border: 0, borderRadius: RADIUS.pill, background: A.white, color: A.ink, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '.12em', cursor: 'pointer', transition: 'background-color .2s, color .2s' }}>
              RECEIPT
            </button>
          )}
        </div>
      )}
    </article>
  );
}

/** SEND MONEY: the peer-transfer flow (search → amount → confirm) in a sheet. */
function SendMoneyPill({ wallet, onSent }) {
  const [openSheet, setOpenSheet] = useState(false);
  const [step, setStep] = useState(1);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [recipient, setRecipient] = useState(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState(null);
  const [sending, setSending] = useState(false);
  const timer = useRef(null);

  const openIt = () => {
    setOpenSheet(true); setStep(1); setSearch(''); setResults([]); setRecipient(null); setAmount(''); setNote(''); setErr(null); setSending(false);
  };
  const close = () => { setOpenSheet(false); if (timer.current) clearTimeout(timer.current); };

  useEffect(() => {
    if (!openSheet) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openSheet]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = (value) => {
    setSearch(value); setErr(null);
    if (timer.current) clearTimeout(timer.current);
    if (value.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    timer.current = setTimeout(async () => {
      const r = await friendService.searchPeople(value.trim(), 10);
      if (r.success) setResults(r.data || []);
      setSearching(false);
    }, 500);
  };

  const review = () => {
    if (!recipient) { setErr('Select a recipient'); return; }
    const n = parseFloat(amount);
    if (Number.isNaN(n) || n < 0.01) { setErr('Minimum amount is $0.01'); return; }
    if (wallet && Math.round(n * 100) > wallet.balance) { setErr('Insufficient balance'); return; }
    setErr(null); setStep(2);
  };

  const send = async () => {
    setSending(true); setErr(null);
    const r = await walletService.sendPCoins(recipient.userId, Math.round(parseFloat(amount) * 100), note.trim() || null);
    if (r.success) { close(); onSent(); } else { setErr(r.error || 'Failed to send'); setSending(false); }
  };

  const person = (p, i, onPick) => (
    <button key={p.userId} type="button" onClick={onPick} className="pr-acct-row-hover" style={{ display: 'grid', gridTemplateColumns: '40px minmax(0,1fr)', gap: 12, alignItems: 'center', width: '100%', padding: 8, border: 0, borderRadius: 14, background: A.white, fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer' }}>
      <Avatar src={p.avatarUrl} name={p.displayName} tint={AVATAR_TINTS[i % AVATAR_TINTS.length]} size={40} radius={12} />
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.displayName}</span>
        {p.handle && <span style={{ fontSize: 11, color: A.grey500 }}>@{p.handle}</span>}
      </span>
    </button>
  );

  return (
    <>
      <Pill kind="secondary" height={44} style={{ padding: '0 22px' }} onClick={openIt}>SEND MONEY</Pill>
      {openSheet && (
        <div role="dialog" aria-modal="true" aria-label={step === 1 ? 'Send money' : 'Confirm transfer'} style={{ position: 'fixed', inset: 0, zIndex: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(0,0,0,.6)' }} onClick={close}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 440, maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16, padding: 24, borderRadius: 28, background: A.white, color: A.ink, fontFamily: 'inherit' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.12em' }}>{step === 1 ? 'SEND MONEY' : 'CONFIRM TRANSFER'}</span>
              <button type="button" onClick={close} aria-label="Close" className="pr-acct-invert" style={{ width: 36, height: 36, border: 0, borderRadius: '50%', background: A.off, fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>×</button>
            </div>

            {step === 1 ? (
              <>
                <FieldLabel>RECIPIENT</FieldLabel>
                {recipient ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 8, borderRadius: 16, background: A.canvas }}>
                    <div style={{ flex: 1, minWidth: 0 }}>{person(recipient, 0, () => {})}</div>
                    <Pill kind="white" style={{ height: 32, padding: '0 12px', fontSize: 10 }} onClick={() => { setRecipient(null); setSearch(''); setResults([]); }}>CHANGE</Pill>
                  </div>
                ) : (
                  <>
                    <Input value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search by name, email, or Instagram…" autoFocus />
                    {searching && <span style={{ fontSize: 10, letterSpacing: '.12em', color: A.grey500 }}>SEARCHING…</span>}
                    {results.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 240, overflowY: 'auto', padding: 4, borderRadius: 16, background: A.canvas }}>
                        {results.map((p, i) => person(p, i, () => { setRecipient(p); setSearch(''); setResults([]); }))}
                      </div>
                    )}
                    {search.trim().length >= 2 && !searching && results.length === 0 && <span style={{ fontSize: 12, color: A.grey500 }}>No one found.</span>}
                  </>
                )}
                <FieldLabel htmlFor="send-amt">AMOUNT ($)</FieldLabel>
                <Input id="send-amt" inputMode="decimal" value={amount} onChange={(e) => { setAmount(e.target.value.replace(/[^\d.]/g, '')); setErr(null); }} placeholder="0.00" style={{ height: 56, fontSize: 22, fontWeight: 700 }} />
                <span style={{ fontSize: 10, letterSpacing: '.08em', color: A.grey500 }}>Available: {money(wallet?.balance)}</span>
                <FieldLabel htmlFor="send-note">NOTE (OPTIONAL)</FieldLabel>
                <Input id="send-note" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What's it for?" />
                {err && <span role="alert" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: A.danger }}>{err}</span>}
                <Pill kind="primary" height={48} style={{ width: '100%', fontSize: 12, letterSpacing: '.16em' }} onClick={review} disabled={!recipient || !amount}>REVIEW TRANSFER</Pill>
              </>
            ) : (
              <>
                <div style={{ padding: 12, borderRadius: 16, background: A.canvas }}>{person(recipient, 0, () => {})}</div>
                <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: A.grey500 }}>SENDING</span>
                  <span style={{ fontSize: 40, fontWeight: 700, lineHeight: 1 }}>{money(Math.round(parseFloat(amount) * 100))}</span>
                  {note.trim() && <span style={{ fontSize: 12, fontStyle: 'italic', color: A.grey500 }}>“{note.trim()}”</span>}
                </div>
                <span style={{ padding: '12px 14px', borderRadius: 14, background: A.dangerFill, color: A.danger, fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textAlign: 'center' }}>
                  ONCE SENT, THIS CAN'T BE TAKEN BACK.
                </span>
                {err && <span role="alert" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: A.danger }}>{err}</span>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <Pill kind="secondary" height={48} style={{ flex: 1 }} onClick={() => { setStep(1); setErr(null); }} disabled={sending}>BACK</Pill>
                  <Pill kind="primary" height={48} style={{ flex: 1 }} onClick={send} disabled={sending}>{sending ? 'SENDING…' : 'CONFIRM SEND'}</Pill>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
